"use client";
import { useCallback, useEffect, useState } from "react";

export const useFetch = <T>(
  fn: (params?: any) => Promise<T>,
  deps: any[] = []
) => {
  const [data, setData] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(
    async (params?: any) => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await fn(params);
        setData(response);
      } catch (err) {
        const message = (err as Error)?.message || "An error occurred";
        setError(message);
      } finally {
        setIsLoading(false);
      }
    },
    [fn]
  );

  useEffect(() => {
    fetchData();
  }, [fetchData, ...deps]);

  const refetch = (params?: any) => fetchData(params);

  return { data, isLoading, error, refetch };
};

export const useFetchWithId = <T>(
  fn: (id: number) => Promise<T>,
  id: number
) => {
  const [data, setData] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async (fetchId: number) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fn(fetchId);
      setData(response);
    } catch (err) {
      const message = (err as Error)?.message || "An error occurred";
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchData(id);
    }
  }, [fn, id]);

  const refetch = (newId?: number) => {
    const fetchId = newId || id;
    if (fetchId) {
      fetchData(fetchId);
    }
  };

  return { data, isLoading, error, refetch };
};
